import { Button, DialogContent, Stack } from "@mui/material";
import {
  AlertsBarContainer,
  AlertsBarVariant,
  AlertsContext,
  DistaDialog,
  DistaDialogHeader,
} from "@sista/dista-ui";
import { IdmClientContext } from "@sista/idm-client";
import {
  Organization,
  SistaId,
  SmartButton,
  useSafeSistaAsync,
} from "@sista/library-stasi";
import { SkeletonProjectData } from "@sista/service-project-skeleton";
import { SistaSpravaUkoluList } from "@sista/service-workflow-ui";
import { nanoid } from "nanoid";
import { useCallback, useContext, useMemo, useState } from "react";
import { FormContainer, useForm } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";

import {
  NoticeFormData,
  OrganizationChangeNoticeFormFields,
} from "./OrganizationChangeNoticeFormFields";
import { useRejstrikClient } from "../../../api/utils/useRejstrikClient";

export type OrganizationChangeNoticeDialogProps = {
  organization: Organization;
  onDone: () => void;
  onCancel: () => void;
};

export const OrganizationChangeNoticeDialog = (
  props: OrganizationChangeNoticeDialogProps
) => {
  const { t } = useTranslation("ucet");
  const { loggedAccount } = useContext(IdmClientContext);
  const { showSuccess } = useContext(AlertsContext);
  const rej = useRejstrikClient();
  const form = useForm<NoticeFormData>();

  const [step, setStep] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<
    SkeletonProjectData[]
  >([]);
  const [projectsError, setProjectsError] = useState(false);

  const changeNoticeId = useMemo(() => nanoid(), []);

  const organizationUrn = useMemo(
    () => new SistaId("organizations", props.organization.id).asUrn(),
    [props.organization.id]
  );

  const changeNoticeUrn = useMemo(
    () => new SistaId("organizationChangeNotice", changeNoticeId).asUrn(),
    [changeNoticeId]
  );

  const fistoContext = useMemo(
    () => [organizationUrn, changeNoticeUrn],
    [organizationUrn, changeNoticeUrn]
  );

  const taskContext = useMemo(
    () => [
      organizationUrn,
      new SistaId("workflows", "organization_change_notice").asUrn(),
      changeNoticeUrn,
    ],
    [organizationUrn, changeNoticeUrn]
  );

  const [createChangeNoticeCall, createChangeNoticeState] = useSafeSistaAsync(
    rej.createOrganizationListChangeNotice
  );

  const onStep1Submit = useCallback(
    async (data: NoticeFormData) => {
      if (data.projectsAffected && selectedProjects.length === 0) {
        setProjectsError(true);
        return;
      }
      const projects = data.projectsAffected
        ? selectedProjects.map((p) => ({
            id: p.id,
            roCfpId: p.roCfpId,
            roNotFundedId: p.roNotFundedId ?? undefined,
            roContractConclusionId: p.roContractConclusionId ?? undefined,
            roImplementationId: p.roImplementationId ?? undefined,
            roEndedId: p.roEndedId ?? undefined,
            providerId: p.providerId ?? undefined,
          }))
        : [];
      const result = await createChangeNoticeCall({
        organizationId: props.organization.id,
        id: changeNoticeId,
        subject: data.subject,
        description: data.description,
        justification: data.justification,
        impacts: data.impacts,
        projects,
        fileIds: data.fileIds ?? [],
      });
      if (result == null) return;
      showSuccess(
        t(
          "ucet.organizationChangeNotice.submitSuccess",
          "Oznámení změny bylo úspěšně odesláno."
        )
      );
      setWaiting(true);
      await new Promise((resolve) => setTimeout(resolve, 5000));
      setWaiting(false);
      setStep(1);
    },
    [
      createChangeNoticeCall,
      props.organization.id,
      changeNoticeId,
      selectedProjects,
      showSuccess,
      t,
    ]
  );

  return (
    <DistaDialog open={true} onClose={props.onCancel}>
      <DistaDialogHeader
        onClose={props.onCancel}
        title={
          step === 0
            ? t(
                "ucet.organizationChangeNotice.dialog.titleStep1",
                "Oznámení změny - krok 1 ze 2"
              )
            : t(
                "ucet.organizationChangeNotice.dialog.titleStep2",
                "Oznámení změny - krok 2 ze 2"
              )
        }
      />
      <DialogContent>
        {step === 0 && (
          <FormContainer formContext={form} onSuccess={onStep1Submit}>
            <Stack spacing={2} sx={{ my: 2 }}>
              <OrganizationChangeNoticeFormFields
                form={form}
                organizationId={props.organization.id}
                authKey={loggedAccount.authKey}
                selectedProjects={selectedProjects}
                onProjectsChange={(projects) => {
                  setSelectedProjects(projects);
                  if (projects.length > 0) setProjectsError(false);
                }}
                projectsError={projectsError}
                fistoContext={fistoContext}
              />
              <Stack direction="row" justifyContent="flex-end">
                <SmartButton
                  state={{
                    ...createChangeNoticeState,
                    pending: createChangeNoticeState.pending || waiting,
                  }}
                  onClick={() => form.handleSubmit(onStep1Submit)()}
                  variant="contained"
                >
                  {t("ucet.organizationChangeNotice.continue", "Pokračovat")}
                </SmartButton>
              </Stack>
              <AlertsBarContainer
                id="alerts-in-dialog"
                variant={AlertsBarVariant.relative}
              />
            </Stack>
          </FormContainer>
        )}

        {step === 1 && (
          <Stack spacing={2}>
            <SistaSpravaUkoluList
              loggedAccount={loggedAccount}
              loggedAuthKey={loggedAccount.authKey}
              tasksContext={taskContext}
              spravaUkoluRoot={"/sprava-ukolu"}
            />
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" onClick={props.onDone}>
                {t("ucet.organizationChangeNotice.close", "Zavřít")}
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </DistaDialog>
  );
};
